import { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useResetPasswordMutation } from "../redux/services/authApi";
import { getApiErrorMessage } from "../utils/apiError";

interface LocationState {
  email: string;
  token: string; 
}

export const usePasswordResetController = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;

  const email = state?.email || "";
  const token = state?.token || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [resetPasswordApi] = useResetPasswordMutation();

  // password validation rules
  const minLength = password.length >= 8;
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>[\]\\\/`~_\-+=;:]/.test(password);
  const passwordValid = minLength && hasNumber && hasSpecial;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const isFormValid = passwordValid && passwordsMatch;

  // submit new passworrd and otp
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isFormValid) return;

      if (!email || !token) {
        setErrorMessage("Invalid request. Missing email or OTP.");
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        const res = await resetPasswordApi({
          email,
          token,
          password,
          password_confirmation: password,
        }).unwrap();

        if (res.status === "success") {
          setShowSuccess(true);
        } else {
          setErrorMessage(typeof res?.data === "string" ? res.data : "Failed to reset password.");
        }
      } catch (err: any) {
        console.error("Password reset error:", err);
        setErrorMessage(getApiErrorMessage(err, "An error occurred while resetting password."));
      } finally {
        setIsLoading(false);
      }
    },
    [password, confirmPassword, email, token, isFormValid, resetPasswordApi]
  );

  const goToLogin = () => navigate("/login");

  return {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    isLoading,
    showSuccess,
    setShowSuccess,
    errorMessage,
    isFormValid,
    handleSubmit,
    goToLogin,
  };
};
